/**
 * Every price the pricing section quotes, as numbers, in one place.
 *
 * `pricing.tsx` renders them and `components/seo/structured-data.tsx` publishes
 * them as schema.org offers. They used to be written out twice — the cards had
 * "฿1,290" hard-coded and the JSON-LD carried a single hand-typed 590 — which
 * is the drift Google's structured-data policy is aimed at: markup quoting a
 * price the page does not show is markup that gets the whole block ignored, or
 * worse. Reading both from here means a price change is one edit and cannot
 * leave the two disagreeing.
 *
 * Currency is THB throughout, so it is not repeated per entry.
 */

/** Thai baht, grouped the way the cards print it: ฿1,290. */
export const thb = (amount: number) => `฿${amount.toLocaleString("en-US")}`;

/**
 * A yearly subscription is twelve monthly ones — there is no annual discount,
 * so the yearly figures the CRM cards quote are derived rather than listed.
 *
 * Written as a function for the same reason every other price on this page is
 * read from here: ฿15,480 typed beside ฿1,290 is two numbers that can disagree,
 * and the one that would be wrong is the one nobody re-checks.
 */
export const yearly = (monthly: number) => monthly * 12;

/**
 * Per-branch monthly subscriptions — the CRM rail and the monthly POS plan.
 *
 * `posMonthlyWas` is the struck-through list price the monthly card shows
 * beside the promotional one; it is a real published price, so the offer
 * markup below reports it as such rather than pretending 590 is the only one.
 */
export const SUBSCRIPTIONS = {
  posMonthly: 590,
  posMonthlyWas: 790,
  /**
   * The restaurant tier, which is the ฿590 plan plus KDS. It lives here rather
   * than only in the product page's copy for the reason the note above gives:
   * /product/pos quotes it in its FAQ *and* on a pricing card, and a figure
   * written twice is a figure that will disagree with itself.
   */
  posProfessional: 990,
  crmStarter: 1290,
  crmPro: 1690,
} as const;

/** One-off hardware kits: tablet + printer + install, paid once. */
export const KITS = {
  starter: 13900,
  business: 21900,
  enterprise: 25900,
} as const;

/**
 * Optional CRM modules, priced per branch on top of whichever plan is chosen.
 *
 * Reservation is offered on all three CRM plans, which is why it sits here
 * rather than on one of them.
 */
export const CRM_ADD_ONS = {
  reservation: 500,
} as const;

/** Optional hardware, priced per kit card as an add-on line. */
export const KIT_ADD_ONS = {
  drawer: 2900,
  scanner: 2500,
} as const;

/**
 * What each plan is CALLED, and the hardware each kit ships with.
 *
 * Here for the same reason the figures above are: these are quoted by the home
 * page's pricing rail and again by /product/crm and /product/pos, and a name
 * written twice drifts exactly like a price does. It already had — the product
 * pages shipped calling the kits "Starter Kit" while the home page called the
 * same three "POS Starter".
 *
 * Not translated, deliberately: they are product names, and the home page has
 * always carried them as literals rather than through the locale bundles.
 */
export const PLAN_NAMES = {
  crmStarter: "Starter Pack",
  crmPro: "Pro Pack",
  crmComplete: "Complete Pack",
  posStarterKit: "POS Starter",
  posBusinessKit: "POS Business",
  posEnterpriseKit: "POS Enterprise",
} as const;

/** Model names for the two devices in each kit — also literals, also shared. */
export const KIT_HARDWARE = {
  starter: {
    tablet: "Redmi Pad SE 8GB/128GB",
    printer: "Printer XP-C300H-USL (LAN)",
  },
  business: {
    tablet: "Redmi Pad Pro 8GB/256GB (12.1”)",
    printer: "Printer XP-C300H-USLW (LAN+WIFI)",
  },
  enterprise: {
    tablet: "Xiaomi Pad 7 (8GB/256GB)",
    printer: "Printer XP-C300H-USLW",
  },
} as const;
